import queryString from 'query-string';
import { createPath } from 'react-router';

import { STUDY_SESSION_TYPES } from 'my-core/study-session-utils';

import { ACT_SLUG, DAT_SLUG, isStdTestCourse, LSAT_SLUG, MCAT_SLUG, SAT_SLUG } from './course-utils';

function getSlugOrId(obj) {
  return typeof obj === 'object' ? obj.slug || obj.id : obj;
}

export function pathToString(path) {
  return typeof path === 'string' ? path : createPath(path || window.location);
}

export const aboutPath = () => '/about';
export const accountSettingsPath = () => '/profile';
export const adminDashboardPath = () => '/admin';
export const aiTutorPath = () => '/ai-tutor';
export const announcementsPath = () => '/announcements';
export const callUsPath = () => '/call-us';
export const textUsPath = () => '/text-us';
export const careersPath = () => '/careers';
export const chatPagePath = chatId => `/chats${chatId ? `/${chatId}` : ''}`;
export const courseAssignmentPath = caOrId => `/course-assignments/${getSlugOrId(caOrId)}`;
export const classRepWizardPath = crc => `/class-rep-wizard/${getSlugOrId(crc)}`;
export const contentCreatorDashboardPath = () => '/content-creator-dashboard';
export const educatorsLandingPath = () => '/for-educators';
export const highSchoolLandingPath = () => '/high-school';
export const instructorsPath = () => '/instructors';
export const instructorDashboardPath = () => '/instructor-dashboard';
export const instructorDashboardOCQuestionPath = ocqId =>
  `${instructorDashboardPath()}?page=oc_questions&ocq_id=${ocqId}&status`;
export const instructorDashboardStudentCourseMessagePath = (courseSlug, messageId) =>
  `${instructorDashboardPath()}?page=${courseSlug}&tab=messages&scm_id=${messageId}`;
export const invitesPagePath = () => '/invites';
export const jobApplicantPath = appId => `/job-application${appId ? `?ja_id=${appId}` : ''}`;
export const livePrepLandingPath = () => '/live-prep';
export const livePrepSchoolPath = school => `/live-prep/schools/${getSlugOrId(school)}`;
export const marketingRepDashboardPath = () => '/marketing-rep-dashboard';
export const mcatRepDashboardPath = () => '/mcat-rep-dashboard';
export const brandAmbassadorDashboardPath = () => '/brand-ambassador-dashboard';
export const mcatEliteLandingPath = () => '/mcat'; // '/mcat-elite-515';
export const mcatFreeResourcesPath = () => '/mcat/free-resources';
export const mcatEventsPath = () => '/mcat/events';
export const mcatEventPath = eventId => `/mcat/events/${eventId}`;
export const mcatExamDates = () => '/mcat/exam-dates';
export const mcatScoresPath = () => '/mcat/scores';
export const mcatScorePath = score => `/mcat/scores/${score}`;
export const mcatSectionScorePath = section => `/mcat/scores/${section}`;
export const mcatSectionBalancePath = () => '/mcat/scores/section-balance';
export const carsPassagesPath = () => '/cars-passages';
export const mcatLandingPath = mcatEliteLandingPath;
export const mcatCoursePagePath = () => '/courses/mcat';
export const mcatLandingPricingPath = () => `${mcatEliteLandingPath()}#pricing`;
export const mcatCarsPassagePath = weekNum => `/cars-passages/week-${weekNum}`;
export const mcatDailyFlashCardsPath = () => '/mcat-flash-cards';
export const mcatExamReportPath = templateId => `/mcat-edr/${templateId}`;
export const medSchoolCalculatorPath = () => '/med-school-calculator';
export const lsatLandingPath = () => '/lsat';
export const lsatSelfPacedLandingPath = () => '/lsat-self-paced';
export const lsatEventsPath = () => '/lsat/events';
export const lsatTutoringLandingPath = () => '/lsat-tutoring';
export const lsatEventPath = eventId => `/lsat/events/${eventId}`;
export const medSchoolAdmissionsLandingPath = () => '/med-school-admissions';
export const preMedTutoringLandingPath = () => '/pre-med-tutoring';
export const datLandingPath = () => '/dat';
export const datEliteLandingPath = () => '/dat-elite';
export const datSelfPacedLandingPath = () => '/dat-self-paced';
export const datEventsPath = () => '/dat/events';
export const datEventPath = eventId => `/dat/events/${eventId}`;
export const datTutoringLandingPath = () => '/dat-tutoring';
export const satLandingPath = () => '/sat';
export const satEventsPath = () => '/sat/events';
export const satEventPath = eventId => `/sat/events/${eventId}`;
export const satTutoringLandingPath = () => '/sat-tutoring';
export const actLandingPath = () => '/act';
export const actEventsPath = () => '/act/events';
export const actEventPath = eventId => `/act/events/${eventId}`;
export const actTutoringLandingPath = () => '/act-tutoring';

export const stdTestCourseLandingPath = courseSlug => {
  switch (courseSlug) {
    case MCAT_SLUG:
      return mcatLandingPath();
    case LSAT_SLUG:
      return lsatLandingPath();
    case DAT_SLUG:
      return datLandingPath();
    case SAT_SLUG:
      return satLandingPath();
    case ACT_SLUG:
      return actLandingPath();
  }
  return '/';
};
export const preMedPlusLandingPath = () => '/pre-med-plus';
export const termsAndConditionsPath = () => '/terms-and-conditions';
export const privacyPolicyPath = () => '/privacy-policy';
export const customProductPath = product => `/custom-checkout/${getSlugOrId(product)}`;

export const flashCardsPath = (slugOrId, returnPath) =>
  `/flash-cards/${getSlugOrId(slugOrId)}${returnPath ? `?returnPath=${returnPath}` : ''}`;
export const flashCardsEditPath = slugOrId => `/flash-cards/${getSlugOrId(slugOrId)}/edit`;

export const lsacPracticeTestPath = testId => `/lsat-practice-tests/${testId}`;
export const scoresmartTestPath = testId => `/hs-exam-practice/practice-exams/${testId}`;
export const scoresmartTestResultsPath = (testId, graph = false) =>
  `/hs-exam-practice/practice-exam-results/${testId}${graph ? '?graph=true' : ''}`;
export const scoresmartExternalTestResultsPath = (testId, submissionId) =>
  `/hs-exam-practice/practice-exam-results/${testId}/scoresmart${submissionId ? `?submission_id=${submissionId}` : ''}`;
export const scoresmartDrillPath = drillId => `/hs-exam-practice/practice-drills/${drillId}`;
export const scoresmartDrillsPath = () => '/hs-exam-practice/practice-drills';
export const scoresmartDashboardPath = () => '/hs-exam-practice/sc-dashboard';

export const onlineCoursePath = ocOrId => `/online-courses/${ocOrId?.id || ocOrId}`;
export const onlineCoursePracticeModePath = ocOrId => `${onlineCoursePath(ocOrId)}/practice-mode`;
export const onlineCoursePracticeModeQuestionPath = (ocOrId, sectId, qId, courseSlug) => ({
  pathname: courseSlug ? studentCoursePagePath(courseSlug) : onlineCoursePath(ocOrId),
  search: queryString.stringify({ qid: qId, sect_id: sectId, ...(courseSlug && { practice_mode: true }) }),
});
export const onlineCourseActivityPath = (ocOrId, actType, actId, courseSlug) => ({
  pathname: courseSlug ? studentCoursePagePath(courseSlug) : onlineCoursePath(ocOrId),
  search: queryString.stringify({ activity_type: actType, activity_id: actId }),
});
export const onlineCourseSectionPath = (ocOrId, sectId, courseSlug) => ({
  pathname: courseSlug ? studentCoursePagePath(courseSlug) : onlineCoursePath(ocOrId),
  search: queryString.stringify({ sect_id: sectId }),
});
export const onlineCourseChapterPath = (ocOrId, chapId) => ({
  pathname: onlineCoursePath(ocOrId),
  search: queryString.stringify({ chap_id: chapId }),
});
export const onlineCourseComponentPath = (ocOrId, compType, compId) => {
  const pathname = onlineCoursePath(ocOrId);
  switch (compType) {
    case 'OnlineCourseChapter':
      return { pathname, search: queryString.stringify({ chap_id: compId }) };
    case 'OnlineCourseSection':
      return { pathname, search: queryString.stringify({ sect_id: compId }) };
    case 'OnlineCourseActivity':
      return { pathname, search: queryString.stringify({ oca_id: compId }) };
    case 'Quiz':
    case 'CourseLesson':
    case 'QuizQuestion':
      return onlineCourseActivityPath(ocOrId, compType, compId);
    default:
      return { pathname };
  }
};
export const onlineCourseQuestionPath = ({
  activity_id,
  activity_type,
  assignment_id,
  course_slug,
  online_course_id,
  online_course_type,
  pm_section_id,
  question_id,
  quiz_question_id,
  reply_id,
  sub_question_id,
}) => {
  const useInCourseUXPath = course_slug && online_course_type === 'class_pass' && !isStdTestCourse(course_slug);
  return {
    pathname:
      assignment_id ? courseAssignmentPath(assignment_id)
      : useInCourseUXPath ? studentCoursePagePath(course_slug)
      : pm_section_id ? onlineCoursePracticeModePath(online_course_id)
      : onlineCoursePath(online_course_id),
    search: queryString.stringify({
      ...(pm_section_id ?
        { sect_id: pm_section_id, qid: activity_id, ...(useInCourseUXPath && { practice_mode: true }) }
      : {
          activity_id,
          activity_type,
        }),
      replyId: reply_id || undefined,
      questionId: question_id || undefined,
      qqId: quiz_question_id || undefined,
      subQId: sub_question_id || undefined,
    }),
  };
};

export const onDemandLandingPath = () => '/on-demand';
export const parentsLandingPath = () => '/for-parents';
export const partnerPath = partner => `/partners/${getSlugOrId(partner)}`;
export const partnershipsPath = () => '/partnerships';
export const subscriptionLandingPath = () => '/wizeprep-plus';
export const scholarshipsPath = () => '/scholarships';
export const scholarshipPath = scholarship => `/scholarships/${getSlugOrId(scholarship)}`;
export const pricingPath = () => '/pricing';

export const schoolPath = school => `/schools/${getSlugOrId(school)}`;
export const schoolsPath = () => searchSchoolsPath();
export const searchPagePath = () => '/search';

const searchTypeQueryPath = (type, query) => ({
  pathname: searchPagePath(),
  search: queryString.stringify({ type, query: query || undefined }),
});
export const searchCoursesPath = query => searchTypeQueryPath('course', query);
export const searchSchoolsPath = query => searchTypeQueryPath('school', query);
export const searchLibraryPath = (query, user) => ({
  pathname: searchPagePath(),
  search: queryString.stringify({
    type: 'topic',
    query: query || undefined,
    sid: user?.school_id,
    school_label: user?.school_name,
  }),
});

export const studentDashboardPath = page => `/dashboard${page ? `?page=${page}` : ''}`;
export const studentDashboardCurrentCoursesPath = () => studentDashboardPath();
export const studentDashboardStudyInitiativesPath = () => studentDashboardPath('study-sessions');
export const studentMcatDashboardPath = page => `/mcat-dashboard${page ? `?page=${page}` : ''}`;
export const studentLsatDashboardPath = page => `/lsat-dashboard${page ? `?page=${page}` : ''}`;
export const studentDatDashboardPath = page => `/dat-dashboard${page ? `?page=${page}` : ''}`;
export const studentSatDashboardPath = page => `/sat-dashboard${page ? `?page=${page}` : ''}`;
export const studentActDashboardPath = page => `/act-dashboard${page ? `?page=${page}` : ''}`;
export const classRepDashboardPath = () => '/class-rep-dashboard';
export const mcatStudySheetsPath = () => `${studentMcatDashboardPath('study-sheets')}`;
export const studentOnboardingPath = params => `/onboarding${params ? `?${queryString.stringify(params)}` : ''}`;
export const studentSignupPath = params => `/sign-up${params ? `?${queryString.stringify(params)}` : ''}`;
export const checkoutPath = params => `/checkout${params ? `?${queryString.stringify(params)}` : ''}`;

export const courseLandingPath = (courseOrSlug, includeDashboard) => {
  if (isStdTestCourse(courseOrSlug)) {
    switch (courseOrSlug.slug || courseOrSlug) {
      case MCAT_SLUG:
        return includeDashboard ? studentMcatDashboardPath() : mcatLandingPath();
      case LSAT_SLUG:
        return includeDashboard ? studentLsatDashboardPath() : lsatLandingPath();
      case DAT_SLUG:
        return includeDashboard ? studentDatDashboardPath() : datLandingPath();
      case SAT_SLUG:
        return includeDashboard ? studentSatDashboardPath() : satLandingPath();
      case ACT_SLUG:
        return includeDashboard ? studentActDashboardPath() : actLandingPath();
    }
  }
  return studentCoursePagePath(courseOrSlug);
};

export const studyPlannerActivityPath = (courseSlug, taskId) => `/study-planner/${courseSlug}/${taskId}`;

export const studyRoomDemoPath = () => '/study-rooms/demo';

export const newStudyInitiativePath = (course, curriculumSources, goal) => {
  const search = queryString.stringify(
    {
      cid: course ? getSlugOrId(course) : undefined,
      csid:
        Array.isArray(curriculumSources) ?
          curriculumSources?.map(getSlugOrId).join(',')
        : getSlugOrId(curriculumSources),
      goal,
    },
    { arrayFormat: 'bracket' },
  );
  return `/study-sessions/new${search ? `?${search}` : ''}`;
};
export const studyInitiativePath = initiative => `/study-sessions/${getSlugOrId(initiative)}`;

export const teacherDashboardPath = () => '/teacher-dashboard';
export const teacherDashboardCourseMembersPath = course => `/teacher-dashboard/courses/${course.id}/students`;
export const teacherDashboardLessonPlannerPath = course => `/teacher-dashboard/courses/${course.id}/planner`;
export const teacherDashboardGradeBookPath = course => `/teacher-dashboard/courses/${course.id}/grade-book`;

export const tutoringDashboardPath = () => '/my-tutoring';
export const tutoringLandingPath = () => '/tutoring';
export const universityLandingPath = () => '/university';
export const tokensDashboardPath = () => '/tokens';
export const coursesPath = filters => ({
  pathname: '/courses',
  search: filters ? queryString.stringify(filters) : undefined,
});

export const essayWriterPath = () => '/wize-writer-dashboard';
export const essayWriterEssayPath = essay => `${essayWriterPath()}/essays/${essay.id}`;
export const essayWriterTemplatePath = template => `${essayWriterPath()}/templates/${template.id}`;
export const essayWriterLandingPagePath = () => '/wize-writer';

export function adminCourseStudentPath(userCourse, email) {
  return `/admin/${userCourse.course.slug}?${queryString.stringify({
    query: email,
    ...(userCourse.course_schedule_id ?
      { schedule_id: userCourse.course_schedule_id }
    : { type: userCourse.status === 'active' ? 'on_demand' : 'trialing' }),
  })}`;
}

export function adminUserPath(userId) {
  return `/admin/users/${userId}`;
}

export function blogPath(blog) {
  return `/blog${blog ? `/${getSlugOrId(blog)}` : ''}`;
}

export function bloggerPagePath(author) {
  return `/blog${author ? '/@' + author.slug : ''}`;
}

export function contestPath() {
  return '/contest';
}

export const coursePath = course => `/courses/${getSlugOrId(course)}`;
export const courseBookletPath = course => `${coursePath(course)}/booklet`;
export const courseClassRepApplyPath = course => `${coursePath(course)}/class-rep-apply`;
export const classRepPositionsPath = () => '/class-rep-positions';

export const onlineCourseCoursePath = oc => `/courses/${oc.course_slug || oc.course?.slug || oc.course_id}#on-demand`;

export const studentCoursePagePath = course => `/in-course-experience/${getSlugOrId(course)}`;
export const studentCoursePageResumeOnDemandPath = course => `${studentCoursePagePath(course)}?resume=true`;
export const studentCoursePageAssessmentPath = (course, assessment) =>
  `${studentCoursePagePath(course)}/${studentCoursePageAssessmentRelativePath(assessment)}`;

export const studentCoursePageAssessmentActivityPath = (course, assessment, activity) =>
  `${assessment ? studentCoursePageAssessmentPath(course, assessment) : `${studentCoursePagePath(course)}/${studentCoursePageOnDemandRelativePath()}`}${activity.fullPath}`;
export const studentCoursePageStudySessionPath = (course, studySession) =>
  `${studentCoursePagePath(course)}/${STUDY_SESSION_TYPES[studySession.type]?.slug}/${studySession.id}`;

export const studentCoursePageAssessmentRelativePath = assessment => `assessments/${getSlugOrId(assessment)}`;
export const studentCoursePageOnDemandRelativePath = () => 'on-demand';
export const studentCoursePageGradeBoostersRelativePath = () => 'grade-boosters';
export const studentCoursePageMockExamRelativePath = oc => `mocks/${getSlugOrId(oc)}`;
export const studentCoursePageFlashCardsRelativePath = deck => `flash-cards/${getSlugOrId(deck)}`;

export function homeworkHelpQuestionsPath() {
  return {
    pathname: studentDashboardPath(),
    search: queryString.stringify({ page: 'ask-a-question' }),
  };
}

export function instructorProfilePath(instructor) {
  return `/instructors/${getSlugOrId(instructor)}`;
}

export function onlineCourseEditPath(ocOrId, search = {}) {
  return {
    pathname: `/online-courses/${ocOrId?.id || ocOrId}/manager/editor`,
    search: queryString.stringify(search),
  };
}
export function onlineCourseManagerPath(ocOrId) {
  return `/online-courses/${ocOrId?.id || ocOrId}/manager`;
}

export function onlineCourseNewPath(defaultParams = {}) {
  return {
    to: '/online-courses/new',
    state: { defaultParams },
  };
}

export function onlineCourseResumePath(ocOrId) {
  return {
    pathname: onlineCoursePath(ocOrId),
    search: 'resume=true',
  };
}

export function onlineCoursePrintPath({ id }) {
  return `/online-courses/${id}/print`;
}

export function tutoringUserPath(user) {
  if (user.guest) return tutoringLandingPath();
  return tutoringDashboardPath();
}

export function tutorProfilePath(slugOrId) {
  return `/instructors/${slugOrId}`;
}

export function tutorSearchPath(subject) {
  return subject ? `/tutors?subject=${subject}` : '/tutors';
}

export function quizQuestionsManagerPath(params) {
  return { pathname: '/online-course-activities/questions', search: queryString.stringify(params) };
}
